/**
 * Chấm thanh điệu tiếng Trung NGAY TRÊN TRÌNH DUYỆT (khối `noi_thanh`, gói nội dung ffuture).
 *
 * Port từ `xem-truoc/pitch.js` của gói CHN101 Bài 1 (bản chỉnh ngày 7/10 — nhận đúng 47/48
 * âm tiết mẫu): YIN dò cao độ → nối khoảng giọng kẹt → sửa quãng tám theo khung lân cận →
 * đặc trưng (đầu/cuối/đáy/đỉnh theo semitone so với âm vực người đọc) → luật phân thanh.
 *
 * Audio KHÔNG rời máy học viên: chỉ decode + tính toán cục bộ. Giới hạn: mới kiểm trên giọng
 * tổng hợp, chưa kiểm định với giọng học viên thật — chỉ dùng cho luyện tập, không tính điểm.
 */

export interface PitchTrack {
  /** Cao độ (Hz) của đoạn hữu thanh chính, đã nội suy khung mất dấu. */
  hz: number[];
  /** Cao độ đã làm mượt của toàn bộ khung (0 = vô thanh). */
  all: number[];
  /** Trung vị cao độ thô (Hz). */
  med: number;
  voicedFrames: number;
}

export interface ToneFeatures {
  start: number;
  end: number;
  min: number;
  max: number;
  minpos: number;
  mean: number;
}

function resample(data: Float32Array, sr: number, to: number): Float32Array {
  if (sr === to) return data;
  const n = Math.floor((data.length * to) / sr);
  const out = new Float32Array(n);
  const r = sr / to;
  for (let i = 0; i < n; i++) {
    const x = i * r;
    const a = Math.floor(x);
    const f = x - a;
    out[i] = (data[a] ?? 0) * (1 - f) + (data[a + 1] ?? 0) * f;
  }
  return out;
}

export function pitchTrack(raw: Float32Array, sr: number): PitchTrack {
  const x = resample(raw, sr, 16000);
  const fs = 16000;
  const N = 480;
  const hop = 160;
  const tMin = Math.floor(fs / 500);
  const tMax = Math.ceil(fs / 65);
  const frames: { r: number; f: number; cv: number }[] = [];
  const cm = new Float32Array(tMax + 2);
  let maxR = 0;
  for (let s = 0; s + N + tMax + 1 < x.length; s += hop) {
    let r = 0;
    for (let i = 0; i < N; i++) r += x[s + i] * x[s + i];
    r = Math.sqrt(r / N);
    if (r > maxR) maxR = r;
    let run = 0;
    cm[0] = 1;
    for (let tau = 1; tau <= tMax + 1; tau++) {
      let sum = 0;
      for (let i = 0; i < N; i++) {
        const dd = x[s + i] - x[s + i + tau];
        sum += dd * dd;
      }
      run += sum;
      cm[tau] = run ? (sum * tau) / run : 1;
    }
    let tau = -1;
    for (let t = tMin; t <= tMax; t++) {
      if (cm[t] < 0.18) {
        while (t + 1 <= tMax && cm[t + 1] < cm[t]) t++;
        tau = t;
        break;
      }
    }
    if (tau < 0) {
      let bt = tMin;
      for (let t = tMin; t <= tMax; t++) if (cm[t] < cm[bt]) bt = t;
      if (cm[bt] < 0.5) tau = bt;
    }
    let f = 0;
    if (tau > 0) {
      const a = cm[tau - 1];
      const b = cm[tau];
      const c = cm[tau + 1];
      const den = a - 2 * b + c;
      const sh = den ? (0.5 * (a - c)) / den : 0;
      f = fs / (tau + Math.max(-1, Math.min(1, sh)));
    }
    frames.push({ r, f, cv: tau > 0 ? cm[tau] : 9 });
  }
  const gate = maxR * 0.08;
  // Khung tuần hoàn kém (giọng kẹt khi rơi thanh 4) chỉ nhận khi đủ to.
  let hz = frames.map((fr) =>
    fr.r > gate && fr.f > 0 && (fr.cv < 0.35 || fr.r > maxR * 0.3) ? fr.f : 0,
  );
  const vals = hz.filter((v) => v > 0).sort((a, b) => a - b);
  const med = vals.length ? vals[Math.floor(vals.length / 2)] : 0;
  // Sửa lỗi quãng tám theo khung lân cận (thanh 4 thật có thể rơi hơn một quãng tám).
  hz = hz.map((v, i) => {
    if (!v) return v;
    const nb: number[] = [];
    for (let k = i - 3; k <= i + 3; k++) if (k !== i && hz[k]) nb.push(hz[k]);
    if (nb.length < 2) return v;
    nb.sort((a, b) => a - b);
    const m = nb[Math.floor(nb.length / 2)];
    if (v > m * 1.8) return v / 2;
    if (v < m * 0.55) return v * 2;
    return v;
  });
  const sm = hz.map((v, i) => {
    if (!v) return 0;
    const w = hz
      .slice(Math.max(0, i - 2), i + 3)
      .filter(Boolean)
      .sort((a, b) => a - b);
    return w[Math.floor(w.length / 2)];
  });
  // Đoạn hữu thanh dài nhất, nối các đoạn lân cận cách nhau ≤ 200 ms.
  const runs: [number, number][] = [];
  let st = -1;
  for (let i = 0; i <= sm.length; i++) {
    if (i < sm.length && sm[i]) {
      if (st < 0) st = i;
    } else if (st >= 0) {
      runs.push([st, i - 1]);
      st = -1;
    }
  }
  let seg: number[] = [];
  if (runs.length) {
    let bi = 0;
    runs.forEach((r, k) => {
      if (r[1] - r[0] > runs[bi][1] - runs[bi][0]) bi = k;
    });
    let lo = runs[bi][0];
    let hi = runs[bi][1];
    for (let k = bi - 1; k >= 0; k--) {
      if (lo - runs[k][1] <= 20 && runs[k][1] - runs[k][0] >= 2) lo = runs[k][0];
      else break;
    }
    for (let k = bi + 1; k < runs.length; k++) {
      if (runs[k][0] - hi <= 20 && runs[k][1] - runs[k][0] >= 2) hi = runs[k][1];
      else break;
    }
    seg = sm.slice(lo, hi + 1);
    // Nội suy khung mất dấu bên trong đoạn (chỗ giọng rơi nhanh của thanh 4).
    for (let i = 0; i < seg.length; i++) {
      if (!seg[i]) {
        let j = i;
        while (j < seg.length && !seg[j]) j++;
        const a = seg[i - 1];
        const b = seg[j] || a;
        for (let k = i; k < j; k++) seg[k] = a * Math.pow(b / a, (k - i + 1) / (j - i + 1));
        i = j;
      }
    }
  }
  return { hz: seg, all: sm, med, voicedFrames: seg.length };
}

export function median(arr: number[]): number {
  const v = arr.filter(Boolean).sort((a, b) => a - b);
  return v.length ? v[Math.floor(v.length / 2)] : 0;
}

/** Đặc trưng đường cao độ theo semitone so với âm vực `ref` (Hz) của người đọc. */
export function feats(f: number[], ref: number): ToneFeatures | null {
  const n = f.length;
  if (n < 6 || !ref) return null;
  const a = Math.floor(n * 0.08);
  const b = Math.max(a + 5, Math.floor(n * 0.95));
  let s = f.slice(a, b).map((v) => 12 * Math.log2(v / ref));
  const k = Math.max(1, Math.floor(s.length / 6));
  const sm: number[] = [];
  for (let i = 0; i + k <= s.length; i++) {
    let t = 0;
    for (let j = 0; j < k; j++) t += s[i + j];
    sm.push(t / k);
  }
  s = sm;
  const q = Math.max(2, Math.floor(s.length / 5));
  const mean = (x: number[]) => x.reduce((p, c) => p + c, 0) / x.length;
  const start = mean(s.slice(0, q));
  const end = mean(s.slice(-q));
  let mi = 0;
  s.forEach((v, i) => {
    if (v < s[mi]) mi = i;
  });
  return { start, end, min: s[mi], max: Math.max(...s), minpos: mi / s.length, mean: mean(s) };
}

/** Luật phân thanh → 1..4, hoặc null nếu chưa rõ. */
export function classify(d: ToneFeatures | null): 1 | 2 | 3 | 4 | null {
  if (!d) return null;
  const { start: st, end: en, min: mn, max: mx, minpos: mp, mean: me } = d;
  const rng = mx - mn;
  if (st - en >= 3.5 && mp > 0.55 && st >= -1.0) return 4;
  if (
    (en - mn >= 1.5 && st - mn >= 1.0 && mp > 0.2 && mp < 0.85 && mn <= -1.5) ||
    (mx <= -0.5 && mn <= -3)
  )
    return 3;
  if (en - st >= 2.5 && mp < 0.5) return 2;
  if (rng < 3.0 && me >= -1.5) return 1;
  return null;
}

let decodeCtx: BaseAudioContext | null = null;

/** Giải mã bản ghi (webm/ogg/mp4/mp3) thành mẫu mono — chạy cục bộ, không gửi đi đâu. */
export async function decodeAudio(buf: ArrayBuffer): Promise<{ data: Float32Array; sr: number }> {
  if (!decodeCtx) {
    const Ctx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) throw new Error("Trình duyệt không hỗ trợ Web Audio");
    decodeCtx = new Ctx();
  }
  const ctx = decodeCtx;
  const audio = await new Promise<AudioBuffer>((resolve, reject) => {
    const pr = ctx.decodeAudioData(buf.slice(0), resolve, reject);
    if (pr && typeof pr.catch === "function") pr.catch(reject);
  });
  return { data: audio.getChannelData(0), sr: audio.sampleRate };
}

/** Điểm (x 0..1, y semitone) để vẽ — chuẩn hoá theo âm vực `ref`. */
export function trackToPoints(hz: number[], ref: number): [number, number][] {
  if (!hz.length || !ref) return [];
  return hz.map((v, i) => [i / Math.max(1, hz.length - 1), 12 * Math.log2(v / ref)]);
}

// ─── Âm vực người đọc: trung vị của 2–3 lượt đọc đầu, lưu theo phiên ───────────

const RANGE_KEY = "ffuture.tone.range.v1";

function readRange(): number[] {
  try {
    const raw = sessionStorage.getItem(RANGE_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? arr.filter((v): v is number => typeof v === "number" && v > 0) : [];
  } catch {
    return [];
  }
}

/** Âm vực tham chiếu: gộp trung vị lượt này vào ≤ 3 lượt đầu của phiên rồi lấy trung vị. */
export function speakerRef(currentMedianHz: number): number {
  const saved = readRange();
  const pool = saved.length < 3 && currentMedianHz > 0 ? [...saved, currentMedianHz] : saved;
  if (pool.length !== saved.length) {
    try {
      sessionStorage.setItem(RANGE_KEY, JSON.stringify(pool));
    } catch {
      // sessionStorage bị chặn — vẫn chấm theo lượt hiện tại.
    }
  }
  return median(pool.length ? pool : [currentMedianHz]);
}
